import axios from 'axios';
import { API_URL } from '@/common/config';
import store from '@/store';

import communicationService from '@/services/PostMessageCommunation/CommunicationService';
import dataKeys from '@/services/PostMessageCommunation/enums/DataKeys';

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (token) {
      prom.resolve(token);
    } else {
      prom.reject(error);
    }
  });

  failedQueue = [];
};

// Axios instance
const http = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
    'Access-Control-Allow-Methods': 'GET,POST,OPTIONS,DELETE,PUT',
  },
});

// Request interceptor
http.interceptors.request.use(
  (config) => {
    store.dispatch('appConfig/startLoading')
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    store.dispatch('appConfig/stopLoading')
    communicationService.sendDataToParent({
      key: dataKeys.ERROR_HANDLER,
      value: JSON.stringify(error),
    });

    return Promise.reject(error);
  }
);

// Response interceptor
http.interceptors.response.use(
  (response) => {
    try {
      if (typeof response.data.result === 'string') {
        response.data.result = JSON.parse(response.data.result);
      }
    } catch {
      // Leave response data as-is
    }
    
    store.dispatch('appConfig/stopLoading')
    return response.data; // Return the parsed data
  },
  async (error) => {
    store.dispatch('appConfig/stopLoading')
    const originalRequest = error.config;

    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return http(originalRequest); // Use http instead of axios
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const currentToken = localStorage.getItem('token');
        const response = await communicationService.requestData({
          key: dataKeys.REFRESH_TOKEN,
          value: currentToken,
        });

        const newToken = response.result_data;

        localStorage.setItem('token', newToken);

        processQueue(null, newToken);
        isRefreshing = false;

        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return http(originalRequest); // Use http instead of axios
      } catch (err) {
        processQueue(err, null);
        isRefreshing = false;

        localStorage.removeItem('token');
        communicationService.sendDataToParent({
          key: dataKeys.ERROR_HANDLER,
          value: JSON.stringify({ message: 'Token refresh failed, user is being logged out.' }),
        });

        return Promise.reject(err);
      }
    } else {
      communicationService.sendDataToParent({
        key: dataKeys.ERROR_HANDLER,
        value: JSON.stringify(error),
      });
    }

    return Promise.reject(error);
  }
);

export default http;
